import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import {
  Box, Typography, TextField, IconButton, CircularProgress,
  Paper, Chip, Collapse, Switch, FormControlLabel, useTheme,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { getModelChatThread, streamModelChatMessage } from "../utils/api";
import { useImpersonation } from "../contexts/ImpersonationContext";

/**
 * Per-deployment "Ask" chat.
 *
 * Default rendering is client-facing: tool calls are hidden, the agent's
 * intermediate work surfaces as a single status line ("Querying your data…"),
 * and the final answer streams in.
 *
 * Admins in self mode see a "Show details" toggle that reveals the full
 * tool-call transcript (fetch, run_sql, run_python). When impersonating, the
 * toggle is hidden — the whole point of impersonation is to see what clients
 * see.
 */

const mdComponents = (theme) => ({
  p: ({ children }) => <p style={{ margin: "0 0 8px", lineHeight: 1.6 }}>{children}</p>,
  h2: ({ children }) => <h2 style={{ fontSize: 15, fontWeight: 700, margin: "12px 0 6px" }}>{children}</h2>,
  h3: ({ children }) => <h3 style={{ fontSize: 14, fontWeight: 700, margin: "10px 0 4px" }}>{children}</h3>,
  ul: ({ children }) => <ul style={{ paddingLeft: 18, margin: "4px 0 8px" }}>{children}</ul>,
  ol: ({ children }) => <ol style={{ paddingLeft: 18, margin: "4px 0 8px" }}>{children}</ol>,
  li: ({ children }) => <li style={{ marginBottom: 4, lineHeight: 1.6 }}>{children}</li>,
  strong: ({ children }) => <strong style={{ fontWeight: 700 }}>{children}</strong>,
  hr: () => <hr style={{ border: "none", borderTop: `1px solid ${theme.palette.divider}`, margin: "10px 0" }} />,
  code: ({ inline, children }) =>
    inline ? (
      <code style={{ background: theme.palette.grey[200], borderRadius: 3, padding: "1px 5px", fontSize: 12, fontFamily: "monospace" }}>
        {children}
      </code>
    ) : (
      <pre style={{ background: theme.palette.grey[100], borderRadius: 4, padding: 8, overflowX: "auto", fontSize: 12, fontFamily: "monospace" }}>
        <code>{children}</code>
      </pre>
    ),
  table: ({ children }) => (
    <div style={{ overflowX: "auto", margin: "8px 0" }}>
      <table style={{ borderCollapse: "collapse", fontSize: 13, width: "100%" }}>{children}</table>
    </div>
  ),
  th: ({ children }) => (
    <th style={{ padding: "6px 12px", background: theme.palette.grey[200], fontWeight: 700, textAlign: "left", border: `1px solid ${theme.palette.divider}` }}>
      {children}
    </th>
  ),
  td: ({ children }) => (
    <td style={{ padding: "6px 12px", border: `1px solid ${theme.palette.divider}` }}>{children}</td>
  ),
});

function ToolCallBlock({ name, input }) {
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const label = name === "fetch" ? "Read " + (input?.location || "")
    : name === "run_sql" ? "SQL query"
    : name === "run_python" ? "Python"
    : name;
  return (
    <Box sx={{ my: 0.75 }}>
      <Chip
        size="small"
        label={label}
        onClick={() => setOpen((o) => !o)}
        icon={open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        sx={{ fontSize: 11, height: 22, bgcolor: theme.palette.grey[100], borderRadius: 1.5 }}
      />
      <Collapse in={open}>
        <Paper variant="outlined" sx={{ mt: 0.5, p: 1, bgcolor: theme.palette.grey[50] }}>
          <pre style={{ margin: 0, fontSize: 11, fontFamily: "monospace", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
            {name === "fetch" ? input?.location :
             name === "run_sql" ? input?.query :
             name === "run_python" ? input?.code :
             JSON.stringify(input, null, 2)}
          </pre>
        </Paper>
      </Collapse>
    </Box>
  );
}

function ToolResultBlock({ content }) {
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  let parsed = null;
  try { parsed = JSON.parse(content); } catch { /* leave as string */ }
  const errStr = (parsed && (parsed.error || parsed.traceback)) || null;
  const summary = errStr
    ? `Result · error: ${String(errStr).split("\n")[0].slice(0, 120)}`
    : `Result · ${(content || "").length} chars`;
  const isError = !!errStr;
  return (
    <Box sx={{ my: 0.5, ml: 1 }}>
      <Chip
        size="small"
        label={summary}
        onClick={() => setOpen((o) => !o)}
        icon={open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        sx={{
          fontSize: 11, height: 22, borderRadius: 1.5,
          bgcolor: isError ? `${theme.palette.error.main}15` : theme.palette.grey[100],
          color: isError ? "error.main" : "text.secondary",
        }}
      />
      <Collapse in={open}>
        <Paper variant="outlined" sx={{ mt: 0.5, p: 1, bgcolor: theme.palette.grey[50] }}>
          <pre style={{ margin: 0, fontSize: 11, fontFamily: "monospace", whiteSpace: "pre-wrap", wordBreak: "break-word", maxHeight: 320, overflowY: "auto" }}>
            {parsed ? JSON.stringify(parsed, null, 2) : (content || "")}
          </pre>
        </Paper>
      </Collapse>
    </Box>
  );
}

function MessageBubble({ message, showDetails }) {
  const theme = useTheme();
  const isUser = message.role === "user";

  const blocks = Array.isArray(message.content)
    ? message.content
    : [{ type: "text", text: message.content || "" }];

  // Client view: tool_use and tool_result blocks are invisible. The streamed
  // status line during generation is the only surface that hints work happened.
  const visibleBlocks = showDetails
    ? blocks
    : blocks.filter((b) => b.type === "text");

  // A user-role message whose blocks were all tool_results becomes empty after
  // filtering — skip it entirely in client view.
  if (visibleBlocks.length === 0) return null;

  // Trailing tool-result chips (a user-role message that's only tool_results):
  // render unobtrusively under the prior assistant bubble in details mode.
  const isOnlyToolResults = blocks.length > 0 && blocks.every((b) => b.type === "tool_result");
  if (isOnlyToolResults) {
    if (!showDetails) return null;
    return (
      <Box sx={{ ml: 1, mt: -1, mb: 1 }}>
        {blocks.map((b, i) => (
          <ToolResultBlock key={i} content={b.content} />
        ))}
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", justifyContent: isUser ? "flex-end" : "flex-start", mb: 1.5 }}>
      <Box
        sx={{
          maxWidth: "82%",
          px: 2, py: 1.5,
          borderRadius: isUser ? "18px 18px 4px 18px" : "18px 18px 18px 4px",
          bgcolor: isUser ? theme.palette.primary.main : theme.palette.grey[100],
          color: isUser ? "white" : "text.primary",
          fontSize: 14, wordBreak: "break-word",
        }}
      >
        {visibleBlocks.map((b, i) => {
          if (b.type === "text") {
            return isUser ? (
              <span key={i} style={{ whiteSpace: "pre-wrap" }}>{b.text}</span>
            ) : (
              <ReactMarkdown key={i} remarkPlugins={[remarkGfm]} components={mdComponents(theme)}>
                {b.text}
              </ReactMarkdown>
            );
          }
          if (b.type === "tool_use") {
            return <ToolCallBlock key={i} name={b.name} input={b.input} />;
          }
          if (b.type === "tool_result") {
            return <ToolResultBlock key={i} content={b.content} />;
          }
          return null;
        })}
      </Box>
    </Box>
  );
}

function StatusBubble({ text }) {
  const theme = useTheme();
  return (
    <Box sx={{ display: "flex", mb: 1.5 }}>
      <Box sx={{
        px: 2, py: 1.25, borderRadius: "18px 18px 18px 4px",
        bgcolor: theme.palette.grey[100],
        display: "flex", alignItems: "center", gap: 1,
        color: "text.secondary", fontSize: 13, fontStyle: "italic",
      }}>
        <CircularProgress size={12} thickness={5} sx={{ color: "text.disabled" }} />
        {text}
      </Box>
    </Box>
  );
}

function StreamingTextBubble({ text }) {
  const theme = useTheme();
  return (
    <Box sx={{ display: "flex", mb: 1.5 }}>
      <Box sx={{
        maxWidth: "82%", px: 2, py: 1.5,
        borderRadius: "18px 18px 18px 4px",
        bgcolor: theme.palette.grey[100], color: "text.primary",
        fontSize: 14, wordBreak: "break-word",
      }}>
        <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents(theme)}>
          {text}
        </ReactMarkdown>
      </Box>
    </Box>
  );
}

const STARTERS = [
  "How accurate has the model been?",
  "What features matter most to its predictions?",
  "Why did my most recent prediction come out that way?",
  "Where has the model been most and least confident?",
];

export default function ModelChat({ projectId }) {
  const theme = useTheme();
  const { isAdmin, isImpersonating } = useImpersonation();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [streamingStatus, setStreamingStatus] = useState("");
  const [streamingText, setStreamingText] = useState("");

  // Admins in SELF mode get the toggle; admins impersonating + non-admins
  // never see it (client view is the only view).
  const detailsToggleVisible = isAdmin && !isImpersonating;
  const [showDetails, setShowDetails] = useState(false);
  useEffect(() => {
    // Default: admins in self mode want details; everyone else wants clean.
    setShowDetails(detailsToggleVisible);
  }, [detailsToggleVisible]);

  const messagesEndRef = useRef(null);
  // streamingText mirror — used inside the onEvent callback to detect whether
  // there's accumulated text to flush when the next status fires. Avoids the
  // stale-closure problem on the React state value.
  const streamingTextRef = useRef("");

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, streamingStatus, streamingText, sending]);

  useEffect(() => {
    if (!projectId) return;
    setLoading(true);
    getModelChatThread(projectId)
      .then((thread) => setMessages(thread.messages || []))
      .catch((e) => setError(e?.response?.data?.error || e.message || "Failed to load thread"))
      .finally(() => setLoading(false));
  }, [projectId]);

  const sendMessage = useCallback(async (msg) => {
    if (!msg || sending) return;
    setInput("");
    setMessages((prev) => [...prev, { role: "user", content: msg }]);
    setSending(true);
    setError("");
    setStreamingStatus("");
    setStreamingText("");
    streamingTextRef.current = "";
    try {
      await streamModelChatMessage(projectId, msg, (event) => {
        if (event.type === "status") {
          // The agent may produce text BEFORE running a tool (e.g.
          // "Let me look at recent predictions…"). Commit that text to
          // the visible history so the next status/answer doesn't wipe
          // it. The final `done` event replaces messages with the
          // canonical persisted thread, so transient messages get
          // reconciled then.
          if (streamingTextRef.current) {
            const flushed = streamingTextRef.current;
            setMessages((prev) => [
              ...prev,
              { role: "assistant", content: flushed },
            ]);
            streamingTextRef.current = "";
            setStreamingText("");
          }
          setStreamingStatus(event.text || "");
        } else if (event.type === "text_delta") {
          setStreamingStatus("");
          streamingTextRef.current += event.text || "";
          setStreamingText((t) => t + (event.text || ""));
        } else if (event.type === "done") {
          setMessages(event.messages || []);
          streamingTextRef.current = "";
          setStreamingStatus("");
          setStreamingText("");
        } else if (event.type === "error") {
          setError(event.client_message || event.error || "Something went wrong");
        }
      });
    } catch (e) {
      setError(e.message || "Send failed");
    } finally {
      setSending(false);
      setStreamingStatus("");
      setStreamingText("");
      streamingTextRef.current = "";
    }
  }, [projectId, sending]);

  const headerHint = useMemo(() => (
    "Ask anything about how this model works, why a prediction came out a certain way, " +
    "or how it has performed on your data."
  ), []);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "calc(100vh - 280px)", minHeight: 480 }}>
      {/* Header */}
      <Box sx={{
        px: 1, pb: 1, borderBottom: `1px solid ${theme.palette.divider}`,
        display: "flex", alignItems: "center", gap: 2, justifyContent: "space-between",
      }}>
        <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
          {headerHint}
        </Typography>
        {detailsToggleVisible && (
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={showDetails}
                onChange={(_, v) => setShowDetails(v)}
              />
            }
            label={<Typography variant="caption" color="text.secondary">Show details</Typography>}
            sx={{ m: 0, "& .MuiFormControlLabel-label": { ml: 0.5 } }}
          />
        )}
      </Box>

      {/* Messages */}
      <Box sx={{ flex: 1, overflowY: "auto", py: 2, px: 1 }}>
        {messages.length === 0 && (
          <Box sx={{ textAlign: "center", color: "text.secondary", py: 6 }}>
            <Typography variant="body2" sx={{ mb: 1.5 }}>Try asking:</Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75, alignItems: "center" }}>
              {STARTERS.map((q) => (
                <Chip
                  key={q}
                  label={q}
                  variant="outlined"
                  size="small"
                  onClick={() => sendMessage(q)}
                  sx={{ cursor: "pointer", fontSize: 12 }}
                />
              ))}
            </Box>
          </Box>
        )}
        {messages.map((m, i) => (
          <MessageBubble key={i} message={m} showDetails={showDetails} />
        ))}
        {streamingStatus && <StatusBubble text={streamingStatus} />}
        {streamingText && <StreamingTextBubble text={streamingText} />}
        <div ref={messagesEndRef} />
      </Box>

      {error && (
        <Box sx={{ px: 1, py: 1, bgcolor: `${theme.palette.error.main}15`, borderRadius: 1, mb: 1 }}>
          <Typography variant="caption" color="error.main">{error}</Typography>
        </Box>
      )}

      {/* Input */}
      <Box sx={{ display: "flex", gap: 1, pt: 1, borderTop: `1px solid ${theme.palette.divider}` }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Ask about the model…"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey && !sending && input.trim()) {
              e.preventDefault();
              sendMessage(input.trim());
            }
          }}
          disabled={sending}
          multiline
          maxRows={4}
          autoComplete="off"
        />
        <IconButton
          color="primary"
          onClick={() => sendMessage(input.trim())}
          disabled={!input.trim() || sending}
        >
          <SendIcon />
        </IconButton>
      </Box>
    </Box>
  );
}
